import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Save, RotateCcw, ExternalLink } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";

const SECTIONS = [
    {
        title: "Sezione Hero (in alto nella home)",
        eyebrow: "Home · Hero",
        fields: [
            {
                key: "home.hero.eyebrow",
                label: "Sopratitolo (eyebrow)",
                hint: "Il testo piccolo sopra al titolo principale",
                type: "text",
            },
            {
                key: "home.hero.title",
                label: "Titolo principale",
                hint: "Il claim principale della pagina",
                type: "textarea",
            },
            {
                key: "home.hero.title_italic",
                label: "Frase in corsivo (parte finale del titolo)",
                hint: 'Es. "dove preferisci."',
                type: "text",
            },
            {
                key: "home.hero.subtitle",
                label: "Sottotitolo / descrizione",
                hint: "Paragrafo introduttivo sotto il titolo",
                type: "textarea",
            },
            {
                key: "home.search.placeholder",
                label: "Placeholder barra di ricerca",
                type: "text",
            },
        ],
    },
    {
        title: "Sezione Negozi",
        eyebrow: "Home · Negozi",
        fields: [
            {
                key: "home.stores.eyebrow",
                label: "Sopratitolo lista negozi",
                type: "text",
            },
            {
                key: "home.stores.title",
                label: "Titolo lista negozi",
                type: "text",
            },
            {
                key: "home.card.cta",
                label: "Testo bottone (su ogni card)",
                hint: 'Es. "Prenota la tua prova"',
                type: "text",
            },
            {
                key: "home.stores.empty_initial",
                label: "Messaggio se non ci sono negozi a sistema",
                type: "textarea",
            },
            {
                key: "home.stores.empty_title",
                label: "Titolo se la ricerca non trova nulla",
                type: "text",
            },
            {
                key: "home.stores.empty_message",
                label: "Messaggio se la ricerca non trova nulla",
                type: "textarea",
            },
        ],
    },
    {
        title: "Footer (pie' di pagina)",
        eyebrow: "Footer",
        fields: [
            { key: "footer.col1.title", label: "Titolo colonna 1", type: "text" },
            { key: "footer.col1.body", label: "Testo colonna 1", type: "textarea" },
            { key: "footer.col2.title", label: "Titolo colonna 2", type: "text" },
            { key: "footer.col2.body", label: "Testo colonna 2", type: "textarea" },
            { key: "footer.col3.title", label: "Titolo colonna 3", type: "text" },
            {
                key: "footer.col3.body",
                label: "Testo colonna 3",
                hint: "A capo = nuova riga",
                type: "textarea",
            },
        ],
    },
];

export default function AdminContent() {
    const { refresh: refreshPublic } = useSiteContent();
    const [data, setData] = useState({});
    const [defaults, setDefaults] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data: r } = await api.get("/admin/content");
        setData(r.current);
        setDefaults(r.defaults);
    };

    useEffect(() => {
        load();
    }, []);

    const handleChange = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put("/admin/content", data);
            toast.success("Contenuti aggiornati. Ricarica la home per vederli live.");
            refreshPublic();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const handleResetField = (k) => {
        setData((prev) => ({ ...prev, [k]: defaults[k] || "" }));
        toast.info("Campo riportato al valore di default — ricorda di salvare");
    };

    const handleResetAll = async () => {
        if (!window.confirm("Resettare TUTTI i testi ai valori di default?")) return;
        try {
            await api.post("/admin/content/reset");
            toast.success("Tutti i testi resettati ai valori di default");
            load();
            refreshPublic();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div>
            <Toaster position="top-center" richColors />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <div className="overline mb-2">Backoffice</div>
                    <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                        Testi della home
                    </h1>
                    <p className="text-sm text-magni-muted mt-1">
                        Modifica tutti i testi visibili sulla home pubblica e nel footer.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="magni-btn-ghost"
                    >
                        <ExternalLink className="w-4 h-4 mr-1" /> Apri la home
                    </a>
                    <button
                        onClick={handleResetAll}
                        className="magni-btn-secondary"
                        style={{ padding: "10px 18px", fontSize: "12px" }}
                    >
                        <RotateCcw className="w-4 h-4 mr-1" /> Reset totale
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-content-btn"
                        className="magni-btn-primary"
                        style={{ padding: "10px 18px", fontSize: "12px" }}
                    >
                        <Save className="w-4 h-4 mr-1" />
                        {saving ? "Salvataggio..." : "Salva modifiche"}
                    </button>
                </div>
            </div>

            <div className="space-y-8">
                {SECTIONS.map((section) => (
                    <div
                        key={section.title}
                        className="bg-white border border-magni-light"
                    >
                        <div className="px-6 py-4 border-b border-magni-light">
                            <div className="overline">{section.eyebrow}</div>
                            <h2 className="font-serif-display text-2xl text-magni-navy">
                                {section.title}
                            </h2>
                        </div>
                        <div className="p-6 space-y-5">
                            {section.fields.map((f) => {
                                const isCustomized =
                                    data[f.key] !== undefined &&
                                    data[f.key] !== defaults[f.key];
                                return (
                                    <div key={f.key}>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy">
                                                {f.label}
                                                {isCustomized && (
                                                    <span className="ml-2 inline-block bg-magni-celeste text-magni-navy px-2 py-0.5 text-[10px] tracking-wider">
                                                        modificato
                                                    </span>
                                                )}
                                            </label>
                                            {isCustomized && (
                                                <button
                                                    onClick={() => handleResetField(f.key)}
                                                    className="text-[11px] uppercase tracking-wider text-magni-muted hover:text-magni-navy"
                                                >
                                                    ripristina default
                                                </button>
                                            )}
                                        </div>
                                        {f.type === "textarea" ? (
                                            <textarea
                                                rows={3}
                                                value={data[f.key] || ""}
                                                onChange={(e) => handleChange(f.key, e.target.value)}
                                                className="magni-input"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={data[f.key] || ""}
                                                onChange={(e) => handleChange(f.key, e.target.value)}
                                                className="magni-input"
                                            />
                                        )}
                                        {f.hint && (
                                            <div className="text-xs text-magni-muted mt-1">
                                                {f.hint}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="sticky bottom-0 mt-8 bg-magni-cream/95 backdrop-blur-sm py-4 border-t border-magni-light flex items-center justify-end gap-3">
                <span className="text-xs text-magni-muted mr-auto">
                    Le modifiche si vedono in real-time sulla home dopo il salvataggio.
                </span>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="magni-btn-primary"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Salvataggio..." : "Salva modifiche"}
                </button>
            </div>
        </div>
    );
}
